import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../services/supabase';
import { useMediaQuery } from '../hooks/useMediaQuery';
import {
  PlayCircle,
  Brain,
  BarChart2,
  LineChart,
  Lock,
  CheckCircle2,
  Activity,
  TrendingUp,
  ArrowRight,
  Layers,
  PieChart,
  Zap,
  ChevronRight,
  MessageCircle
} from 'lucide-react';

// ─────────────────────────────────────────────────────────
//  Inline SVG Mockups for How It Works section
// ─────────────────────────────────────────────────────────

const FundamentalMockup = () => (
  <svg viewBox="0 0 420 220" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
    <rect width="420" height="220" fill="#0A0E17" rx="8" />
    <rect x="0" y="0" width="420" height="32" fill="#0F1521" rx="4" />
    <circle cx="18" cy="16" r="5" fill="#D4A537" opacity="0.9" />
    <text x="30" y="21" fill="#D4A537" fontSize="9" fontWeight="700" letterSpacing="1" fontFamily="monospace">FUNDAMENTAL ANALYSIS — EURUSD</text>
    <rect x="370" y="8" width="40" height="16" rx="4" fill="#10B981" opacity="0.15" />
    <text x="384" y="20" fill="#10B981" fontSize="7" fontWeight="700" fontFamily="monospace">LIVE</text>
    <text x="16" y="56" fill="#888" fontSize="8" fontFamily="monospace" letterSpacing="1">DIRECTIONAL BIAS</text>
    <rect x="16" y="62" width="160" height="8" rx="4" fill="#1A2035" />
    <rect x="16" y="62" width="112" height="8" rx="4" fill="url(#biasGrad)" />
    <defs>
      <linearGradient id="biasGrad" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#D4A537" />
        <stop offset="100%" stopColor="#10B981" />
      </linearGradient>
    </defs>
    <text x="182" y="70" fill="#10B981" fontSize="11" fontWeight="700" fontFamily="monospace">70% BULLISH</text>
    {[
      { label: 'GDP', val: '+2.1%', color: '#10B981', x: 16, y: 86 },
      { label: 'CPI', val: '3.2%', color: '#D4A537', x: 85, y: 86 },
      { label: 'NFP', val: '227K', color: '#10B981', x: 154, y: 86 },
      { label: 'RATE', val: '5.25%', color: '#D4A537', x: 223, y: 86 },
    ].map(({ label, val, color, x, y }) => (
      <g key={label}>
        <rect x={x} y={y} width="62" height="28" rx="6" fill="#111827" stroke={color} strokeWidth="0.6" strokeOpacity="0.5" />
        <text x={x + 6} y={y + 11} fill="#888" fontSize="7" fontFamily="monospace">{label}</text>
        <text x={x + 6} y={y + 22} fill={color} fontSize="9" fontWeight="700" fontFamily="monospace">{val}</text>
      </g>
    ))}
    <text x="16" y="132" fill="#888" fontSize="8" fontFamily="monospace" letterSpacing="1">CENTRAL BANK DIVERGENCE</text>
    {[
      { label: 'FED', pct: 0.82, color: '#10B981' },
      { label: 'ECB', pct: 0.55, color: '#D4A537' },
      { label: 'BOJ', pct: 0.22, color: '#ef4444' },
      { label: 'BOE', pct: 0.67, color: '#10B981' },
    ].map(({ label, pct, color }, i) => (
      <g key={label}>
        <text x="16" y={150 + i * 16} fill="#666" fontSize="8" fontFamily="monospace">{label}</text>
        <rect x="40" y={141 + i * 16} width="230" height="7" rx="3" fill="#1A2035" />
        <rect x="40" y={141 + i * 16} width={230 * pct} height="7" rx="3" fill={color} opacity="0.7" />
        <text x="276" y={150 + i * 16} fill={color} fontSize="8" fontWeight="700" fontFamily="monospace">{Math.round(pct * 100)}%</text>
      </g>
    ))}
    <rect x="300" y="48" width="106" height="156" rx="8" fill="#111827" stroke="#D4A537" strokeWidth="0.5" strokeOpacity="0.4" />
    <text x="313" y="65" fill="#D4A537" fontSize="8" fontWeight="700" letterSpacing="1" fontFamily="monospace">AI SUMMARY</text>
    <line x1="308" y1="70" x2="398" y2="70" stroke="#D4A537" strokeWidth="0.4" strokeOpacity="0.4" />
    {['• Hawkish Fed', '• ECB pivot', '• Risk-on mood', '• Strong NFP', '• USD weakness', '• Upside bias'].map((t, i) => (
      <text key={i} x="310" y={85 + i * 17} fill="#aaa" fontSize="8" fontFamily="monospace">{t}</text>
    ))}
    <rect x="310" y="178" width="86" height="18" rx="4" fill="#10B981" opacity="0.15" />
    <text x="330" y="191" fill="#10B981" fontSize="8" fontWeight="700" fontFamily="monospace">LONG BIAS ↑</text>
    <ellipse cx="210" cy="220" rx="180" ry="20" fill="#D4A537" opacity="0.04" />
  </svg>
);

const OrderFlowMockup = () => (
  <svg viewBox="0 0 420 220" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
    <rect width="420" height="220" fill="#0A0E17" rx="8" />
    <rect x="0" y="0" width="420" height="32" fill="#0F1521" rx="4" />
    <circle cx="18" cy="16" r="5" fill="#10B981" opacity="0.9" />
    <text x="30" y="21" fill="#10B981" fontSize="9" fontWeight="700" letterSpacing="1" fontFamily="monospace">ORDER FLOW — XAUUSD  (INST. ZONES)</text>
    <text x="16" y="50" fill="#888" fontSize="8" letterSpacing="1" fontFamily="monospace">PRICE LEVEL</text>
    <text x="120" y="50" fill="#888" fontSize="8" letterSpacing="1" fontFamily="monospace">VOLUME</text>
    <text x="210" y="50" fill="#888" fontSize="8" letterSpacing="1" fontFamily="monospace">TYPE</text>
    <text x="290" y="50" fill="#888" fontSize="8" letterSpacing="1" fontFamily="monospace">BAR</text>
    {[
      { price: '2353.00', vol: '315K', type: 'SELL OB', color: '#ef4444', pct: 0.38 },
      { price: '2351.75', vol: '842K', type: 'SELL OB', color: '#ef4444', pct: 0.55 },
      { price: '2350.00 ◀', vol: '1.24M', type: 'CURRENT', color: '#D4A537', pct: 0.85, hl: true },
      { price: '2348.25', vol: '920K', type: 'BUY OB', color: '#10B981', pct: 0.72 },
      { price: '2346.50', vol: '1.10M', type: 'BUY OB', color: '#10B981', pct: 0.80 },
      { price: '2344.00', vol: '288K', type: 'FVG', color: '#6366f1', pct: 0.28 },
    ].map(({ price, vol, type, color, pct, hl }, i) => (
      <g key={price}>
        {hl && <rect x="8" y={56 + i * 22} width="404" height="20" rx="3" fill="#D4A537" fillOpacity="0.06" />}
        <text x="16" y={70 + i * 22} fill={hl ? '#fff' : '#ccc'} fontSize="9" fontWeight={hl ? '700' : '400'} fontFamily="monospace">{price}</text>
        <text x="120" y={70 + i * 22} fill={color} fontSize="9" fontWeight="700" fontFamily="monospace">{vol}</text>
        <rect x="210" y={58 + i * 22} width="60" height="13" rx="3" fill={color} fillOpacity="0.12" />
        <text x="214" y={69 + i * 22} fill={color} fontSize="7" fontWeight="700" fontFamily="monospace">{type}</text>
        <rect x="290" y={61 + i * 22} width={100 * pct} height="8" rx="2" fill={color} opacity="0.5" />
      </g>
    ))}
    <ellipse cx="210" cy="220" rx="180" ry="20" fill="#10B981" opacity="0.04" />
  </svg>
);

const CotMockup = () => (
  <svg viewBox="0 0 420 220" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
    <rect width="420" height="220" fill="#0A0E17" rx="8" />
    <rect x="0" y="0" width="420" height="32" fill="#0F1521" rx="4" />
    <circle cx="18" cy="16" r="5" fill="#6366f1" opacity="0.9" />
    <text x="30" y="21" fill="#aaa" fontSize="9" fontWeight="700" letterSpacing="1" fontFamily="monospace">COMMITMENT OF TRADERS — EURUSD</text>
    <text x="16" y="50" fill="#888" fontSize="8" letterSpacing="1" fontFamily="monospace">NET POSITIONING (LARGE SPECULATORS)</text>
    {[
      { pair: 'EUR/USD', net: +68, color: '#10B981' },
      { pair: 'GBP/USD', net: +44, color: '#10B981' },
      { pair: 'USD/JPY', net: -72, color: '#ef4444' },
      { pair: 'AUD/USD', net: -28, color: '#ef4444' },
      { pair: 'XAU/USD', net: +81, color: '#D4A537' },
    ].map(({ pair, net, color }, i) => {
      const abs = Math.abs(net);
      const barW = abs * 1.6;
      const positive = net > 0;
      return (
        <g key={pair}>
          <text x="16" y={72 + i * 26} fill="#bbb" fontSize="9" fontFamily="monospace">{pair}</text>
          <rect x="96" y={60 + i * 26} width="220" height="14" rx="3" fill="#1A2035" />
          {positive
            ? <rect x="96" y={60 + i * 26} width={barW} height="14" rx="3" fill={color} opacity="0.65" />
            : <rect x={96 + 220 - barW} y={60 + i * 26} width={barW} height="14" rx="3" fill={color} opacity="0.65" />
          }
          <text x="322" y={72 + i * 26} fill={color} fontSize="9" fontWeight="700" fontFamily="monospace">{positive ? '+' : ''}{net}K</text>
          <rect x="360" y={62 + i * 26} width="48" height="12" rx="3" fill={color} fillOpacity="0.1" />
          <text x="366" y={72 + i * 26} fill={color} fontSize="8" fontWeight="700" fontFamily="monospace">{positive ? 'LONG ↑' : 'SHORT ↓'}</text>
        </g>
      );
    })}
    <rect x="16" y="186" width="386" height="24" rx="6" fill="#111827" stroke="#D4A537" strokeWidth="0.4" strokeOpacity="0.5" />
    <text x="26" y="202" fill="#888" fontSize="8" fontFamily="monospace">WK CHANGE:</text>
    <text x="90" y="202" fill="#10B981" fontSize="9" fontWeight="700" fontFamily="monospace">EUR +12K ↑</text>
    <text x="155" y="202" fill="#ef4444" fontSize="9" fontWeight="700" fontFamily="monospace">JPY -8K ↓</text>
    <text x="216" y="202" fill="#D4A537" fontSize="9" fontWeight="700" fontFamily="monospace">GOLD +6K ↑</text>
    <text x="282" y="202" fill="#888" fontSize="8" fontFamily="monospace">CFTC REPORT: FRI 15:30 EST</text>
    <ellipse cx="210" cy="220" rx="180" ry="20" fill="#6366f1" opacity="0.04" />
  </svg>
);

const SignalsMockup = () => (
  <svg viewBox="0 0 420 220" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
    <rect width="420" height="220" fill="#0A0E17" rx="8" />
    <rect x="0" y="0" width="420" height="32" fill="#0F1521" rx="4" />
    <circle cx="18" cy="16" r="5" fill="#D4A537" opacity="0.9" />
    <text x="30" y="21" fill="#D4A537" fontSize="9" fontWeight="700" letterSpacing="1" fontFamily="monospace">AI MASTER SIGNALS  —  TODAY 3 ACTIVE</text>
    {[
      { pair: 'XAUUSD', dir: 'LONG ↑', entry: '2348.50', sl: '2339.00', tp: '2371.00', conf: '91%', color: '#10B981', bgColor: '#10B98110', x: 10, y: 40 },
      { pair: 'EURUSD', dir: 'LONG ↑', entry: '1.08820', sl: '1.08320', tp: '1.09750', conf: '86%', color: '#10B981', bgColor: '#10B98110', x: 146, y: 40 },
      { pair: 'USDJPY', dir: 'SHORT ↓', entry: '149.850', sl: '150.450', tp: '148.250', conf: '78%', color: '#ef4444', bgColor: '#ef444410', x: 282, y: 40 },
    ].map(({ pair, dir, entry, sl, tp, conf, color, bgColor, x, y }) => (
      <g key={pair}>
        <rect x={x} y={y} width="128" height="170" rx="8" fill="#111827" stroke={color} strokeWidth="0.8" strokeOpacity="0.5" />
        <rect x={x} y={y} width="128" height="30" rx="8" fill={bgColor} />
        <text x={x + 10} y={y + 14} fill="#fff" fontSize="11" fontWeight="800" fontFamily="monospace">{pair}</text>
        <rect x={x + 72} y={y + 6} width="50" height="18" rx="4" fill={color} fillOpacity="0.18" />
        <text x={x + 78} y={y + 19} fill={color} fontSize="9" fontWeight="800" fontFamily="monospace">{dir}</text>
        <text x={x + 10} y={y + 52} fill="#888" fontSize="7" fontFamily="monospace">ENTRY</text>
        <text x={x + 10} y={y + 64} fill="#fff" fontSize="11" fontWeight="700" fontFamily="monospace">{entry}</text>
        <text x={x + 10} y={y + 84} fill="#888" fontSize="7" fontFamily="monospace">STOP LOSS</text>
        <text x={x + 10} y={y + 96} fill="#ef4444" fontSize="10" fontWeight="700" fontFamily="monospace">{sl}</text>
        <text x={x + 10} y={y + 116} fill="#888" fontSize="7" fontFamily="monospace">TAKE PROFIT</text>
        <text x={x + 10} y={y + 128} fill="#10B981" fontSize="10" fontWeight="700" fontFamily="monospace">{tp}</text>
        <text x={x + 10} y={y + 148} fill="#888" fontSize="7" fontFamily="monospace">AI CONFIDENCE</text>
        <rect x={x + 10} y={y + 152} width="108" height="5" rx="2" fill="#1A2035" />
        <rect x={x + 10} y={y + 152} width={108 * parseInt(conf) / 100} height="5" rx="2" fill={color} opacity="0.7" />
        <text x={x + 86} y={y + 157} fill={color} fontSize="8" fontWeight="700" fontFamily="monospace">{conf}</text>
      </g>
    ))}
    <ellipse cx="210" cy="220" rx="180" ry="20" fill="#D4A537" opacity="0.04" />
  </svg>
);

// Golden Spotlight Component for Pricing Section
const SpotlightGold = () => (
  <div
    style={{
      width: "100%",
      display: "flex",
      justifyContent: "center",
      position: "relative",
      transform: "translateY(28px)",
      marginBottom: "-10px",
      zIndex: 0,
      pointerEvents: "none"
    }}
  >
    <div
      style={{
        width: "clamp(300px, 45vw, 600px)",
        height: "120px",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        overflow: "hidden" /* prevent blur from spilling too far sides */
      }}
    >
      {/* The horizontal crisp light source line */}
      <div
        style={{
          width: "48%",
          height: "2px",
          background: "linear-gradient(90deg, transparent 0%, rgba(212, 165, 55, 1) 20%, #ffe066 50%, rgba(212, 165, 55, 1) 80%, transparent 100%)",
          boxShadow: "0 0 15px 3px rgba(212,165,55,0.5)",
          zIndex: 3,
        }}
      />

      {/* The downward volumetric beam */}
      <div
        style={{
          width: "100%",
          height: "100%",
          background: "linear-gradient(to bottom, rgba(212, 165, 55, 0.4) 0%, rgba(212, 165, 55, 0.05) 50%, transparent 100%)",
          clipPath: "polygon(26% 0, 74% 0, 100% 100%, 0% 100%)",
          filter: "blur(12px)",
          marginTop: "-1px",
          zIndex: 2,
        }}
      />

      {/* Inner brighter core for the glow */}
      <div
        style={{
          position: "absolute",
          top: "1px",
          width: "90%",
          height: "90%",
          background: "linear-gradient(to bottom, rgba(255, 224, 102, 0.45) 0%, rgba(212, 165, 55, 0.1) 60%, transparent 100%)",
          clipPath: "polygon(28% 0, 72% 0, 95% 100%, 5% 100%)",
          filter: "blur(8px)",
          zIndex: 2,
        }}
      />
    </div>
  </div>
);

// How It Works steps data
const STEPS = [
  {
    icon: Brain,
    tag: 'FUNDAMENTAL ANALYSIS',
    title: 'Know the Bias Before You Trade',
    desc: 'AI synthesizes macro data — GDP, CPI, NFP, central bank policy, and sentiment — into a clear directional bias so you know whether to buy or sell before you even open a chart.',
    img: '/assets/fund-desktop.png.webp', // Using desktop image for consistency
  },
  {
    icon: BarChart2,
    tag: 'ORDER FLOW',
    title: 'See Where Smart Money Is Waiting',
    desc: 'AI maps institutional order blocks, fair value gaps, and liquidity zones — the exact price levels where hedge funds and banks have placed their orders.',
    img: '/assets/order-desktop.png.jpg',
  },
  {
    icon: Activity,
    tag: 'COT REPORT',
    title: 'Trade With Hedge Fund Positioning',
    desc: 'Every week the CFTC publishes how the largest speculators are positioned. ForexFund translates that raw data into a simple, visual dashboard — so you trade with institutional momentum, not against it.',
    img: '/assets/cot-desktop.png.webp',
  },
  {
    icon: TrendingUp,
    tag: 'AI MASTER SIGNALS',
    title: 'Just Give Me the Trades',
    desc: 'When fundamental bias, order flow, and COT all align, ForexFund issues a precise signal with exact Entry, Stop Loss, and Take Profit — no interpretation needed.',
    img: '/assets/signal-desktop.png.webp',
  },
];


const LazyVideo: React.FC<{
  src?: string;
  sources?: { src: string; type: string }[];
  poster?: string;
  className?: string;
  style?: React.CSSProperties;
  ariaLabel?: string;
  priority?: boolean;
  onEnded?: () => void;
  fetchPriority?: 'high' | 'low' | 'auto';
  stuckAt?: number;
}> = ({ src, sources, poster, className, style, ariaLabel, priority, onEnded, fetchPriority, stuckAt }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          videoRef.current?.play().catch(() => { });
        } else {
          videoRef.current?.pause();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '800px'
      }
    );
    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (videoRef.current && isInView) {
      videoRef.current.load();
      videoRef.current.play().catch(() => { });
    }
  }, [sources, isInView]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || stuckAt === undefined) return;

    const handleTimeUpdate = () => {
      if (video.currentTime >= stuckAt) {
        video.pause();
        video.removeEventListener('timeupdate', handleTimeUpdate);
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, [stuckAt, isInView]);

  return (
    <video
      ref={videoRef}
      className={className}
      style={style}
      poster={(isInView || priority) ? poster : undefined}
      muted
      loop={!onEnded}
      playsInline
      onEnded={onEnded}
      preload={priority ? "auto" : "metadata"}
      aria-label={ariaLabel}
      {...(fetchPriority ? { fetchpriority: fetchPriority } : {})}
    >
      {(isInView || priority) && (
        <>
          {sources ? (
            sources.map((s, i) => <source key={i} src={s.src} type={s.type} />)
          ) : (
            src && <source src={src} type="video/mp4" />
          )}
        </>
      )}
    </video>
  );
};

export const Landing: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const isDesktop = useMediaQuery('(min-width: 768px)');


  // Academy Playlist State
  const academyVideos = [
    [
      { src: "https://res.cloudinary.com/deofymjel/video/upload/q_auto,f_auto/v1774144687/vedio1_dydfvj.webm", type: "video/webm" },
      { src: "https://res.cloudinary.com/deofymjel/video/upload/q_auto,f_auto/v1774144687/vedio1_dydfvj.mp4", type: "video/mp4" }
    ],
    [
      { src: "https://res.cloudinary.com/deofymjel/video/upload/q_auto,f_auto/v1774144688/Video2_vbjabc.webm", type: "video/webm" },
      { src: "https://res.cloudinary.com/deofymjel/video/upload/q_auto,f_auto/v1774144688/Video2_vbjabc.mp4", type: "video/mp4" }
    ]
  ];
  const [currentAcademyVideo, setCurrentAcademyVideo] = useState(0);

  const [loading, setLoading] = useState<string | null>(null);

  const PLANS_DATA = {
    'Pro': 'https://nowpayments.io/payment/?iid=5164894137',
    'Signals': 'https://nowpayments.io/payment/?iid=4328694785',
    'Premium': 'https://nowpayments.io/payment/?iid=6137395487',
    'Elite': 'https://nowpayments.io/payment/?iid=6191855210'
  };

  const handleAcademyVideoEnded = () => {
    setCurrentAcademyVideo((prev) => (prev + 1) % academyVideos.length);
  };

  const handleSubscribe = async (planKey: 'Pro' | 'Signals' | 'Premium' | 'Elite') => {
    setLoading(planKey);
    try {
      const redirectUrl = PLANS_DATA[planKey];
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        navigate(`/login?view=signup&redirect_to=${encodeURIComponent(redirectUrl)}`);
        return;
      }

      window.location.href = redirectUrl;
    } catch (err) {
      console.error(err);
      setLoading(null);
    }
  };

  const handlePricingNavigation = () => {
    navigate('/pricing');
  };

  return (
    <div className="min-h-screen bg-[#060810] text-white font-sans selection:bg-[#D4A537] selection:text-black">
      <Helmet>
        <title>ForexFund AI — Institutional Trading Terminal & AI Fundamental Analysis</title>
        <meta name="description" content="Gain a decisive professional edge with institutional-grade AI fundamental analysis, real-time gold signals, and liquidity maps. Trade with smart money intelligence today." />
        <link rel="canonical" href="https://www.forexfundai.com/" />

        {/* Financial Product Schema */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FinancialProduct",
              "name": "ForexFund AI Analyst",
              "description": "An institutional-grade trading terminal that provides real-time macro fundamentals, COT report insights, institutional order flow maps, and algorithmic market forecasts to give traders a professional edge.",
              "brand": {
                "@type": "Brand",
                "name": "ForexFund"
              }
            }
          `}
        </script>

        {/* Organization Schema */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "ForexFundAI",
              "url": "https://www.forexfundai.com",
              "logo": {
                "@type": "ImageObject",
                "url": "https://zhyekkoguoxhvryucmwk.supabase.co/storage/v1/object/public/website-assets/icon_only.png?format=webp&quality=80",
                "width": 512,
                "height": 512
              },
              "description": "AI-powered institutional trading intelligence for retail forex and gold traders.",
              "sameAs": [
                "https://twitter.com/forexfundai",
                "https://t.me/forexfundai"
              ],
              "contactPoint": {
                "@type": "ContactPoint",
                "contactType": "customer support",
                "url": "https://www.forexfundai.com/support",
                "availableLanguage": "English"
              }
            }
          `}
        </script>

        {/* FAQPage Schema */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "What is ForexFundAI?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "ForexFundAI is an AI-powered institutional trading platform that gives retail traders hedge-fund-level analysis including real-time Gold (XAU/USD) signals, COT report dashboards, institutional order flow maps, and macro fundamental analysis — starting at $30/month."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How accurate are ForexFundAI Gold signals?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "ForexFundAI Gold (XAU/USD) signals use a 3-layer validation process: macro fundamental bias, institutional order flow analysis, and COT positioning confirmation. Only setups reaching 90%+ AI confidence are delivered to Elite subscribers."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Does ForexFundAI work with MetaTrader 5?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes. Premium and Elite plans include full MT5 auto-execution. The ForexFund_AutoTrader expert advisor automatically executes AI-verified signals on your MetaTrader 5 terminal with dynamic position sizing."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What is the difference between Pro, Premium, and Elite plans?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Pro ($30/mo) provides 70 AI fundamental analyses with COT and order flow — no signals. Premium ($50/mo) adds 3 daily Gold signals, Telegram alerts, and MT5 auto-execution. Elite ($100/mo) offers 250 analyses, 20+ instruments, priority delivery, and 90%+ confidence signals."
                  }
                }
              ]
            }
          `}
        </script>
      </Helmet>
      <style>{`
        :root {
            --gold: #D4A537;
            --inst-green: #10B981;
            --inst-red: #ef4444;
        }
        @keyframes ticker-scroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
        }
        .header-ticker-wrap {
            width: 100%;
            overflow: hidden;
            background: rgba(6, 8, 16, 0.95);
            backdrop-filter: blur(12px);
            border-bottom: 1px solid rgba(255, 255, 255, 0.05);
            position: fixed;
            top: 0;
            left: 0;
            z-index: 100;
        }
        .header-ticker-move {
            display: inline-flex;
            white-space: nowrap;
            animation: ticker-scroll 40s linear infinite;
        }
        .header-ticker-item {
            display: inline-flex;
            align-items: center;
            padding: 8px 32px;
            font-family: 'Inter', sans-serif;
            font-weight: 600;
            font-size: 11px;
            letter-spacing: 0.5px;
            gap: 8px;
        }
        .hero-grid {
            background-size: 40px 40px;
            background-image:
                linear-gradient(to right, rgba(255, 255, 255, 0.03) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
            mask-image: linear-gradient(to bottom, black 40%, transparent 100%);
        }
        .glass-card {
            background-color: rgba(255, 255, 255, 0.02);
            backdrop-filter: blur(24px);
            border: 1px solid rgba(255, 255, 255, 0.1);
        }
        .btn-gold {
            background: linear-gradient(135deg, #E0B040, #D4A537, #A07820);
            color: #000;
            text-transform: uppercase;
            letter-spacing: 1px;
            font-weight: 800;
            transition: all 0.3s ease;
            box-shadow: 0 0 20px rgba(212,165,55,0.3);
        }
        .btn-gold:hover {
            box-shadow: 0 0 30px rgba(212, 165, 55, 0.6);
            transform: translateY(-1px);
            filter: brightness(1.1);
        }
        .candlestick {
            width: 6px;
            background: #10B981;
            position: relative;
        }
        .candlestick.bear {
            background: #ef4444;
        }
        .candlestick::before {
            content: '';
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            width: 1px;
            height: 140%;
            top: -20%;
            background: inherit;
            z-index: -1;
        }
        .text-finance-accent {
          color: #D4A537; /* Assuming finance-accent maps to gold */
        }
        .bg-finance-accent {
          background-color: #D4A537;
        }
        .border-finance-accent {
          border-color: #D4A537;
        }
      `}</style>

      {/* Hero Section */}
      <section className="relative h-screen min-h-[700px] flex items-center justify-center overflow-hidden bg-[#0A0E17]">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#D4A537]/10 blur-[120px] rounded-full pointer-events-none z-[1]"></div>
        <div className="absolute inset-0 z-0">
          <img
            src="https://res.cloudinary.com/deofymjel/video/upload/so_0/v1774256150/hero_vedio_sxhm89.jpg"
            alt="ForexFund Trading Terminal"
            className="w-full h-full object-cover opacity-80"
            loading="eager"
          />
          <div className="absolute inset-0 bg-[#0A0E17]/60"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#060810]/80 via-transparent to-[#0A0E17]"></div>
        </div>
        <div className="absolute inset-0 hero-grid pointer-events-none z-[2]"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center flex flex-col items-center justify-center pt-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-8 border border-white/10 rounded-full bg-white/5 backdrop-blur-md">
            <span className="w-2 h-2 rounded-full bg-[#D4A537] animate-pulse shadow-[0_0_10px_#D4A537]"></span>
            <span className="font-bold text-[11px] tracking-[2px] uppercase text-[#D4A537]">AI Confidence: 94.2%</span>
          </div>
          <h1 className="text-5xl md:text-8xl font-black text-white mb-6 tracking-tight leading-none max-w-5xl drop-shadow-2xl">
            Trade With An <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F0C060] via-[#D4A537] to-[#A07820] filter drop-shadow-[0_0_20px_rgba(212,165,55,0.3)]">Institutional Edge</span>
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-lg text-gray-300 font-normal tracking-wide mb-10 leading-relaxed bg-white/[0.03] backdrop-blur-xl p-6 rounded-2xl border border-white/10">
            ForexFund synthesizes macro data, central bank policy, and institutional sentiment into a single directional bias.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 w-full px-4">
            <button className="btn-gold px-10 py-4 rounded-lg text-base text-center cursor-pointer" onClick={handlePricingNavigation}>
              Unlock Your Edge
            </button>
            <button onClick={() => navigate('/about')} className="px-8 py-4 rounded-lg border border-white/10 bg-white/[0.05] backdrop-blur-md text-white hover:bg-white/10 hover:border-white/20 transition-all duration-300 font-bold tracking-wider uppercase text-sm flex items-center justify-center gap-2 shadow-lg">
              <PlayCircle className="w-5 h-5 text-[#D4A537]" /> How It Works
            </button>
          </div>
        </div>
      </section>

      {/* USP Ticker Tape */}
      <div className="w-full bg-[#080B12] border-t border-b border-white/5 py-4 overflow-hidden relative flex items-center z-20" style={{ boxShadow: 'inset 0 0 20px rgba(0,0,0,0.8)' }}>
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-[#080B12] to-transparent z-10 pointer-events-none"></div>
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-[#080B12] to-transparent z-10 pointer-events-none"></div>

        <div className="flex whitespace-nowrap" style={{ animation: 'ticker-scroll 50s linear infinite', width: 'max-content' }}>
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex shrink-0 items-center">
              {[
                { icon: "⚡", pre: "24/5 AI", gold: " Market ", post: "Scanning" },
                { icon: "📊", pre: "Institutional", gold: " Order ", post: "Flow" },
                { icon: "🎯", pre: "Verified", gold: " Hybrid ", post: "Signals" },
                { icon: "🏦", pre: "CFTC COT", gold: " Data ", post: "Tracking" },
                { icon: "🧠", pre: "Central Bank", gold: " Divergence", post: "" },
                { icon: "📈", pre: "Real-Time", gold: " Macro ", post: "Events" },
                { icon: "🏛️", pre: "Smart", gold: " Money ", post: "Concepts" },
                { icon: "💼", pre: "Dark Pool", gold: " Liquidity ", post: "Zones" },
                { icon: "🔍", pre: "Algorithmic", gold: " Pattern ", post: "Recognition" },
                { icon: "🌐", pre: "Global", gold: " Sentiment ", post: "Analysis" }
              ].map((usp, idx) => (
                <div key={idx} className="flex items-center">
                  <span className="font-bold tracking-wider text-[13px] uppercase flex items-center gap-2">
                    <span>{usp.icon}</span>
                    <span>
                      <span className="text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.4)]">{usp.pre}</span>
                      <span className="text-[#D4A537] drop-shadow-[0_0_8px_rgba(212,165,55,0.5)]">{usp.gold}</span>
                      <span className="text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.4)]">{usp.post}</span>
                    </span>
                  </span>
                  <div className="w-1.5 h-1.5 rounded-full bg-[#D4A537] mx-10 shadow-[0_0_10px_#D4A537]"></div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Features Section */}
      <section className="py-32 bg-gradient-to-b from-[#0A0E17] to-[#0F1419] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-5xl font-black mb-4 tracking-tight">
              <span className="text-white">The 4 Pillars of </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F0C060] via-[#D4A537] to-[#A07820]">Institutional Trading</span>
            </h2>
            <div className="w-32 h-[1px] bg-gradient-to-r from-transparent via-[#D4A537] to-transparent mx-auto opacity-50"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* Fundamental Analysis Card */}
            <div className="group relative glass-card rounded-2xl overflow-hidden transition-all duration-500 hover:border-[#D4A537]/40 hover:-translate-y-2 hover:shadow-[0_10px_40px_-10px_rgba(212,165,55,0.2)]">
              <div className="relative overflow-hidden h-80 md:h-[400px] bg-[#0A0E17]">
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1F2E] via-transparent to-transparent z-10 pointer-events-none"></div>

                {/*
                  ── SEO / Accessibility description ──────────────────────────────────
                  These videos are a live screen recording of the ForexFund Fundamental
                  Analysis dashboard. The recording demonstrates how the AI aggregates
                  real-time macroeconomic data — including GDP growth rate, CPI inflation
                  figures, Non-Farm Payrolls (NFP), and interest-rate decisions — to
                  calculate a directional bias score for a currency pair. It shows the
                  Central Bank Divergence chart comparing the Federal Reserve, ECB, BOJ,
                  and BOE stances, and the AI Summary panel that outputs a clear
                  BULLISH or BEARISH bias with exact confidence percentage, giving
                  traders an institutional-grade macro edge before they open a chart.
                  ──────────────────────────────────────────────────────────────────── */}

                {isDesktop ? (
                  <LazyVideo
                    src="https://zhyekkoguoxhvryucmwk.supabase.co/storage/v1/object/public/website-assets/fund%20desk.mp4"
                    poster="/assets/fund-desktop.png.webp"
                    className="w-full h-full object-cover"
                    stuckAt={1}
                    ariaLabel="Screen recording of the ForexFund Fundamental Analysis dashboard showing real-time GDP, CPI, NFP and Central Bank divergence data"
                  />
                ) : (
                  <LazyVideo
                    src="https://zhyekkoguoxhvryucmwk.supabase.co/storage/v1/object/public/website-assets/fund%20mob.mp4"
                    poster="/assets/fund-mobile.png.webp"
                    className="w-full h-full object-cover"
                    stuckAt={1}
                    ariaLabel="Mobile screen recording of the ForexFund Fundamental Analysis tool"
                  />
                )}
              </div>
              <div className="p-8 relative z-20 mt-[-20px]">
                <h3 className="text-2xl font-bold text-white mb-2 tracking-tight flex items-center gap-3">
                  <Brain className="w-6 h-6 text-[#D4A537]" />
                  Real-time Macro Fundamentals
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  Watch the AI scan live macroeconomic data — GDP growth, CPI inflation, NFP job figures, and central bank interest-rate decisions — then score each currency's fundamental strength. The result is a clear <span className="text-[#10B981] font-semibold">Bullish</span> or <span className="text-[#ef4444] font-semibold">Bearish</span> directional bias so you know exactly which side of the market to trade before you even open a chart.
                </p>
                <Link to="/insights" className="inline-flex items-center gap-1.5 text-xs font-black text-[#D4A537] uppercase tracking-widest hover:gap-3 transition-all group">
                  Explore Macro Data <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* COT Card */}
            <div className="group relative glass-card rounded-2xl overflow-hidden transition-all duration-500 hover:border-[#D4A537]/40 hover:-translate-y-2 hover:shadow-[0_10px_40px_-10px_rgba(212,165,55,0.2)]">
              <div className="relative overflow-hidden h-80 md:h-[400px] bg-[#0A0E17]">
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1F2E] via-transparent to-transparent z-10 pointer-events-none"></div>

                {/*
                  ── SEO / Accessibility description ──────────────────────────────────
                  These videos show the ForexFund Commitment of Traders (COT) Report
                  dashboard. The recording demonstrates how the platform visualises
                  weekly CFTC data — the official U.S. Commodity Futures Trading
                  Commission report — to display the net long and net short positioning
                  of large institutional speculators (hedge funds and asset managers)
                  across major forex pairs including EUR/USD, GBP/USD, USD/JPY,
                  AUD/USD, and Gold (XAU/USD). The dashboard highlights week-over-week
                  positioning changes and trend-confirmation signals so traders can
                  align their entries with institutional momentum rather than fighting
                  the smart-money flow.
                  ──────────────────────────────────────────────────────────────────── */}

                {isDesktop ? (
                  <LazyVideo
                    src="https://zhyekkoguoxhvryucmwk.supabase.co/storage/v1/object/public/website-assets/cot%20desk.mp4"
                    poster="/assets/cot-desktop.png.webp"
                    className="w-full h-full object-cover"
                    stuckAt={1}
                    ariaLabel="Screen recording of the ForexFund Commitment of Traders (COT) dashboard"
                  />
                ) : (
                  <LazyVideo
                    src="https://zhyekkoguoxhvryucmwk.supabase.co/storage/v1/object/public/website-assets/cot%20mob.mp4"
                    poster="/assets/cot-mobile.png.webp"
                    className="w-full h-full object-cover"
                    stuckAt={1}
                    ariaLabel="Mobile screen recording of the ForexFund COT Report tool"
                  />
                )}
              </div>
              <div className="p-8 relative z-20 mt-[-20px]">
                <h3 className="text-2xl font-bold text-white mb-2 tracking-tight flex items-center gap-3">
                  <Activity className="w-6 h-6 text-[#D4A537]" />
                  COT Report Insights
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  Track the positioning of institutional traders and large speculators to identify where the smart money is flowing. Our dashboard provides a clear, visual representation of the Commitment of Traders (COT) report, allowing you to align your trades with the underlying market momentum.
                </p>
                <Link to="/insights" className="inline-flex items-center gap-1.5 text-xs font-black text-[#D4A537] uppercase tracking-widest hover:gap-3 transition-all group">
                  View COT Analysis <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* Order Flow Card */}
            <div className="group relative glass-card rounded-2xl overflow-hidden transition-all duration-500 hover:border-[#D4A537]/40 hover:-translate-y-2 hover:shadow-[0_10px_40px_-10px_rgba(212,165,55,0.2)]">
              <div className="relative overflow-hidden h-80 md:h-[400px] bg-[#0A0E17]">
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1F2E] via-transparent to-transparent z-10 pointer-events-none"></div>

                {/*
                  ── SEO / Accessibility description ──────────────────────────────────
                  These videos are a screen recording of the ForexFund Order Flow
                  Analysis dashboard. The recording shows the AI mapping institutional
                  order blocks (buy-side and sell-side), fair value gaps (FVG), and
                  liquidity zones across intraday and higher time-frame charts for
                  instruments such as Gold (XAU/USD), EUR/USD, and GBP/USD. Each
                  price level is annotated with estimated institutional volume, order
                  type (BUY OB / SELL OB / FVG), and a volume bar so traders can
                  pinpoint the exact price levels where hedge funds and central banks
                  have queued large orders — enabling precise, low-risk entries aligned
                  with smart-money positioning.
                  ──────────────────────────────────────────────────────────────────── */}

                {isDesktop ? (
                  <LazyVideo
                    src="https://zhyekkoguoxhvryucmwk.supabase.co/storage/v1/object/public/website-assets/order%20desk.mp4"
                    poster="/assets/order-desktop.png.jpg"
                    className="w-full h-full object-cover"
                    stuckAt={1}
                    ariaLabel="Screen recording of the ForexFund Order Flow dashboard"
                  />
                ) : (
                  <LazyVideo
                    src="https://zhyekkoguoxhvryucmwk.supabase.co/storage/v1/object/public/website-assets/order%20mob.mp4"
                    poster="/assets/order-mobile.png.jpg"
                    className="w-full h-full object-cover"
                    stuckAt={1}
                    ariaLabel="Mobile screen recording of the ForexFund Order Flow tool"
                  />
                )}
              </div>
              <div className="p-8 relative z-20 mt-[-20px]">
                <h3 className="text-2xl font-bold text-white mb-2 tracking-tight flex items-center gap-3">
                  <BarChart2 className="w-6 h-6 text-[#D4A537]" />
                  Institutional Order Flow Maps
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  Gain a critical advantage by visualizing the exact price levels where institutional orders are clustered. Our order flow maps reveal the hidden supply and demand zones that drive market movements, enabling you to make more precise entries and exits.
                </p>
                <Link to="/insights" className="inline-flex items-center gap-1.5 text-xs font-black text-[#D4A537] uppercase tracking-widest hover:gap-3 transition-all group">
                  Analyze Order Flow <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* Price History Card */}
            <div className="group relative glass-card rounded-2xl overflow-hidden transition-all duration-500 hover:border-[#D4A537]/40 hover:-translate-y-2 hover:shadow-[0_10px_40px_-10px_rgba(212,165,55,0.2)]">
              <div className="relative overflow-hidden h-80 md:h-[400px] bg-[#060810]">
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1F2E] via-transparent to-transparent z-10 pointer-events-none"></div>

                {/*
                  ── SEO / Accessibility description ──────────────────────────────────
                  These videos show the ForexFund Price History & Technical Analysis
                  module in action. The recording demonstrates how traders can select
                  any major forex pair or commodity — such as EUR/USD, GBP/USD,
                  USD/JPY, or Gold (XAU/USD) — and instantly view a clean 1-year
                  historical price chart with built-in technical overlays: key support
                  and resistance levels, moving averages, trend-line detection, and an
                  AI-generated technical summary. The feature maps historical price
                  structure and momentum so traders understand the broader trend context
                  before executing any position.
                  ──────────────────────────────────────────────────────────────────── */}

                {isDesktop ? (
                  <LazyVideo
                    src="https://zhyekkoguoxhvryucmwk.supabase.co/storage/v1/object/public/website-assets/tech%20desk.mp4"
                    poster="/assets/signal-desktop.png.webp"
                    className="w-full h-full object-cover"
                    stuckAt={1}
                    ariaLabel="Screen recording of the ForexFund Price History and Technical Analysis dashboard"
                  />
                ) : (
                  <LazyVideo
                    src="https://zhyekkoguoxhvryucmwk.supabase.co/storage/v1/object/public/website-assets/tech%20mob.mp4"
                    poster="/assets/signal-mobile.png.webp"
                    className="w-full h-full object-cover"
                    stuckAt={1}
                    ariaLabel="Mobile screen recording of the ForexFund technical chart module"
                  />
                )}
              </div>
              <div className="p-8 relative z-20 mt-[-20px]">
                <h3 className="text-2xl font-bold text-white mb-2 tracking-tight flex items-center gap-3">
                  <TrendingUp className="w-6 h-6 text-[#D4A537]" />
                  Algorithmic Market Forecasts
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  Leverage the power of our proprietary AI to receive high-probability market forecasts. Our algorithms analyze a vast array of data points to provide you with a clear and actionable outlook on future price movements.
                </p>
                <Link to="/insights" className="inline-flex items-center gap-1.5 text-xs font-black text-[#D4A537] uppercase tracking-widest hover:gap-3 transition-all group">
                  See AI Predictions <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>

          {/* Hybrid Master Signals */}
          <section className="mt-32">
            {/* Section Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 mb-5 border border-[#10B981]/20 rounded-full bg-[#10B981]/5">
                <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
                <span className="text-[11px] font-black tracking-[2px] uppercase text-[#10B981]">Currently Live — Gold (XAU/USD) Signals</span>
              </div>
              <h2 className="text-4xl md:text-6xl font-black mb-4 uppercase tracking-tight">
                <span className="text-white">HYBRID </span>
                <span className="text-[#D4A537]">MASTER SIGNALS</span>
              </h2>
              <p className="text-gray-400 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-4">
                Our quant engine scans the market 24/5. When fundamental bias, institutional order flow, and COT positioning all align — we issue a precise Entry, SL, and TP. No noise. No guesswork. Just the setup.
              </p>
              {/* Punch Lines */}
              <div className="flex flex-wrap justify-center gap-3 mb-8">
                {[
                  { icon: '🎯', text: '3-Layer AI Validation' },
                  { icon: '💰', text: 'XAU/USD — Gold Only' },
                  { icon: '📲', text: 'Telegram Alerts Included' },
                  { icon: '⚡', text: 'Autotrade Ready' },
                  { icon: '📊', text: 'Avg. Risk:Reward 1:2+' },
                ].map((tag, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border border-white/10 bg-white/[0.04] text-gray-300">
                    <span>{tag.icon}</span> {tag.text}
                  </span>
                ))}
              </div>
            </div>
            {/* Image */}
            <div className="relative w-full max-w-5xl mx-auto group">
              <div className="absolute inset-0 bg-[#D4A537] blur-[120px] opacity-20 pointer-events-none"></div>
              <div className="relative z-10 glass-card rounded-2xl p-2">
                <img loading="lazy" src="/assets/signal-desktop.png.webp" alt="ForexFund Gold Signals Dashboard" className="hidden md:block w-full h-[500px] object-contain object-center rounded-xl bg-[#0A0E17]" />
                {/* Note: Converted this 5.8MB PNG to highly compressed WebP format for fast delivery */}
                <img loading="lazy" src="/assets/signal-mobile.png.webp" alt="ForexFund Gold Signals Mobile" className="block md:hidden w-full h-[500px] object-contain object-center rounded-xl bg-[#0A0E17]" />
              </div>
            </div>
            {/* Signal Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 max-w-3xl mx-auto">
              {[
                { val: '24/5', label: 'Market Scanning' },
                { val: '89%', label: 'AI Bias Accuracy' },
                { val: '1:2+', label: 'Risk-Reward Ratio' },
                { val: '<2s', label: 'Signal Delivery' },
              ].map(s => (
                <div key={s.label} className="text-center bg-white/[0.03] border border-white/5 rounded-xl py-4">
                  <div className="text-2xl font-black text-[#D4A537]">{s.val}</div>
                  <div className="text-[11px] text-gray-500 uppercase tracking-widest font-bold mt-1">{s.label}</div>
                </div>
              ))}
            </div>
            {/* CTA Links */}
            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
              <Link to="/features/standard-signals" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-[#10B981]/30 bg-[#10B981]/10 text-[#10B981] font-black text-sm uppercase tracking-wider hover:bg-[#10B981]/20 transition-all">
                Standard Signals <ChevronRight className="w-4 h-4" />
              </Link>
              <Link to="/features/elite-signals" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-[#D4A537]/30 bg-[#D4A537]/10 text-[#D4A537] font-black text-sm uppercase tracking-wider hover:bg-[#D4A537]/20 transition-all">
                Elite Signals <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </section>

          {/* ── AUTOTRADE SECTION ──────────────────────────────────────────── */}
          <section className="mt-32">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-3 py-1 mb-5 border border-[#D4A537]/20 rounded-full bg-[#D4A537]/5">
                <Zap className="w-3 h-3 text-[#D4A537] fill-[#D4A537]" />
                <span className="text-[11px] font-black tracking-[2px] uppercase text-[#D4A537]">Autotrade — Hands-Free Execution</span>
              </div>
              <h2 className="text-4xl md:text-6xl font-black mb-4 uppercase tracking-tight">
                <span className="text-white">ZERO SCREENS. </span>
                <span className="text-[#D4A537]">FULL PROFIT.</span>
              </h2>
              <p className="text-gray-400 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
                Connect ForexFund to your MetaTrader 5 account. When the AI generates a signal, it is executed automatically — correct lot size, correct entry, correct stop. You just review the results.
              </p>
            </div>

            {/* How Autotrade Works */}
            <div className="max-w-4xl mx-auto">
              <div className="relative pl-8 border-l-2 border-[#D4A537]/20 space-y-8">
                {[
                  { icon: '🧠', title: 'AI generates 3-layer validated signal', desc: 'Fundamental bias + order flow + COT all align. The AI issues the signal with Entry, SL, and TP.' },
                  { icon: '📲', title: 'Instant notification', desc: 'Signal appears on your ForexFund dashboard and pushes to your Telegram in under 2 seconds.' },
                  { icon: '⚡', title: 'Autotrade executes on MT5', desc: 'Your connected MT5 account receives the order automatically. Lot size calculated by your equity risk setting.' },
                  { icon: '📈', title: 'Trade runs — emotionless and precise', desc: 'No manual intervention. No emotional second-guessing. The position runs until SL or TP is hit.' },
                  { icon: '📋', title: 'Auto trade-log generated', desc: 'Every executed signal is recorded in your trade log with P&L, confidence score, and risk-reward result.' },
                ].map((step, i) => (
                  <div key={i} className="relative flex items-start gap-5 group">
                    <div className="absolute -left-[33px] w-4 h-4 rounded-full border-2 border-[#D4A537] bg-[#060810] flex items-center justify-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#D4A537]" />
                    </div>
                    <div className="text-2xl shrink-0">{step.icon}</div>
                    <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4 flex-1 hover:border-[#D4A537]/20 transition-all text-left">
                      <h3 className="font-black text-white text-sm mb-1">{step.title}</h3>
                      <div className="text-gray-400 text-xs leading-relaxed">{step.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-10 text-center">
                <button onClick={() => navigate('/pricing')} className="btn-gold px-8 py-4 rounded-xl text-sm font-black uppercase tracking-widest inline-flex items-center gap-2 hover:scale-105 transition-all">
                  <Zap className="w-4 h-4" /> Activate Autotrade
                </button>
              </div>
            </div>
          </section>
        </div>
      </section>

      {/* How It Works Section */}
      <section
        id="how-it-works"
        className="py-32 relative overflow-hidden"
        style={{ background: 'linear-gradient(180deg,#0F1419 0%,#0A0E17 100%)' }}
      >
        {/* Ambient glow */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle,rgba(212,165,55,0.06) 0%,transparent 70%)', filter: 'blur(60px)' }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

          {/* Section header */}
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 px-3 py-1 mb-6 border border-white/10 rounded-full bg-white/5 backdrop-blur-md">
              <span className="w-2 h-2 rounded-full bg-[#D4A537] animate-pulse shadow-[0_0_8px_#D4A537]" />
              <span className="text-[11px] font-bold tracking-[2px] uppercase text-[#D4A537]">The ForexFund Edge</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-black mb-4 uppercase tracking-tight">
              <span className="text-white">How It </span>
              <span
                className="text-transparent bg-clip-text"
                style={{ backgroundImage: 'linear-gradient(90deg,#F0C060,#D4A537,#A07820)' }}
              >
                Works
              </span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
              Four layers of institutional intelligence — stacked into one dashboard.
            </p>
            <div
              className="w-32 h-px mx-auto mt-6 opacity-50"
              style={{ background: 'linear-gradient(90deg,transparent,#D4A537,transparent)' }}
            />
          </div>

          {isDesktop ? (
            /* 1. Desktop Experience (Animated Vertical Step Tracking) */
            <div className="hidden md:block relative max-w-5xl mx-auto mb-32">
              <div className="absolute left-[39px] top-10 bottom-10 w-[2px] bg-white/[0.03]"></div>
              <div
                className="absolute left-[39px] top-10 w-[2px] bg-gradient-to-b from-transparent via-[#D4A537] to-transparent transition-all duration-1000 ease-out z-20"
                style={{
                  height: `${(activeStep / (STEPS.length - 1)) * 100}%`,
                  boxShadow: '0 0 15px rgba(212,165,55,0.4)',
                }}
              ></div>

              <div className="space-y-12">
                {STEPS.map((s, i) => (
                  <div
                    key={i}
                    onMouseEnter={() => setActiveStep(i)}
                    className={`relative flex items-start gap-12 group cursor-pointer transition-all duration-500 ${activeStep === i ? 'opacity-100 scale-100' : 'opacity-40 scale-[0.98]'
                      }`}
                  >
                    <div
                      className={`relative z-30 w-20 h-20 rounded-2xl border flex items-center justify-center transition-all duration-500 ${activeStep === i
                        ? 'bg-finance-accent border-finance-accent shadow-[0_0_30px_rgba(212,165,55,0.4)] rotate-0'
                        : 'bg-white/5 border-white/10 rotate-3 group-hover:rotate-0'
                        }`}
                    >
                      <s.icon
                        className={`w-8 h-8 transition-colors duration-500 ${activeStep === i ? 'text-black' : 'text-gray-500'
                          }`}
                      />
                    </div>

                    <div className="flex-1 pt-2">
                      <div className="flex items-center gap-4 mb-3">
                        <span className="text-finance-accent font-black text-xs tracking-[4px] uppercase">
                          Step 0{i + 1}
                        </span>
                        <div className="h-px w-12 bg-white/10"></div>
                      </div>
                      <h3 className="text-3xl font-black text-white mb-4 group-hover:text-finance-accent transition-colors">
                        {s.title}
                      </h3>
                      <p className="text-gray-400 text-lg max-w-2xl leading-relaxed">
                        {s.desc}
                      </p>
                    </div>

                    <div
                      className={`w-1/3 rounded-2xl overflow-hidden border border-white/5 bg-white/5 transition-all duration-700 ${activeStep === i ? 'translate-x-0 opacity-100' : 'translate-x-12 opacity-0'
                        }`}
                    >
                      <img src={s.img} alt={s.title} className="w-full h-48 object-cover grayscale hover:grayscale-0 transition-all duration-1000" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* 2. Mobile Experience (Scrollable Cards) — Optimised for DOM Size */
            <div className="md:hidden space-y-4 px-2">
              {STEPS.map((s, i) => (
                <div key={i} className="bg-white/[0.03] border border-white/5 rounded-2xl p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-finance-accent flex items-center justify-center">
                      <s.icon className="w-6 h-6 text-black" />
                    </div>
                    <span className="text-[11px] font-bold tracking-[2px] uppercase text-finance-accent">
                      Step 0{i + 1} — {s.tag}
                    </span>
                  </div>
                  <h3 className="text-xl font-black text-white mb-2">{s.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed mb-6">{s.desc}</p>
                  <img src={s.img} alt={s.title} className="w-full h-48 object-cover rounded-xl grayscale" />
                </div>
              ))}
            </div>
          )}

          {/* Bottom CTA strip */}
          <div
            className="mt-20 rounded-2xl p-8 md:p-12 text-center border"
            style={{
              background: 'linear-gradient(135deg,rgba(212,165,55,0.06),rgba(6,8,16,0.95))',
              borderColor: 'rgba(212,165,55,0.2)',
            }}
          >
            <p className="text-[#D4A537] text-xs font-bold tracking-[3px] uppercase mb-3">Start Today</p>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
              Ready to Trade Like a Hedge Fund?
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto mb-8">
              Join thousands of retail traders who use ForexFund to get institutional-grade analysis — at a fraction of the cost of Bloomberg.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/pricing')}
                className="px-10 py-4 rounded-lg font-black text-sm tracking-[2px] uppercase text-black transition-all duration-300"
                style={{ background: 'linear-gradient(135deg,#F0C060,#D4A537,#A07820)', boxShadow: '0 0 30px rgba(212,165,55,0.3)' }}
                onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.filter = 'none'; e.currentTarget.style.transform = 'none'; }}
              >
                View Pricing Plans
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-8 py-4 rounded-lg font-bold text-sm tracking-widest uppercase text-white transition-all duration-300"
                style={{ border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')}
              >
                View Live Dashboard
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Blurred Dashboard Sneak Peek */}
      <section className="py-32 bg-gradient-to-b from-[#0F1419] to-[#0A0E17] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none" style={{ backgroundImage: "url('https://www.transparenttextures.com/patterns/carbon-fibre.png')" }}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black mb-6 uppercase tracking-tight">
              <span className="text-white">SEE WHAT YOU'RE </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F0C060] to-[#A07820]">MISSING</span>
            </h2>
            <p className="text-gray-400 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
              Stop trading blind. Get the exact fundamental bias, institutional order flow, and AI signals used by top prop-firm traders.
            </p>
          </div>

          <div className="relative w-full max-w-5xl mx-auto glass-card rounded-xl overflow-hidden shadow-[0_0_60px_-15px_rgba(0,0,0,0.8)]">
            {/* Header of Mock Dashboard */}
            <div className="flex justify-between items-center px-4 md:px-6 py-4 border-b border-white/5 bg-white/[0.03]">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-[#10B981] rounded-full animate-pulse shadow-[0_0_15px_#10B981]"></div>
                <span className="text-xs md:text-sm text-white tracking-widest font-bold uppercase">Live Feed: XAUUSD</span>
              </div>
              <div className="flex gap-4 text-[11px] md:text-xs text-gray-500 font-bold">
                <span className="px-2 py-1 rounded bg-black/40 border border-white/5">H1</span>
                <span className="px-2 py-1 rounded bg-black/40 border border-[#D4A537] text-[#D4A537] shadow-[0_0_10px_rgba(212,165,55,0.2)]">H4</span>
                <span className="px-2 py-1 rounded bg-black/40 border border-white/5">D1</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 min-h-[550px] relative">
              {/* Left Sidebar Mockup */}
              <div className="md:col-span-4 border-r border-white/5 p-6 bg-black/20 flex flex-col gap-8">
                <div className="bg-black/40 p-5 rounded-lg border border-white/5 backdrop-blur-sm">
                  <h3 className="font-bold text-xs text-[#D4A537] mb-4 tracking-widest uppercase flex items-center gap-2">
                    <Brain className="w-4 h-4" />
                    Fundamental Analysis
                  </h3>
                  <div className="relative flex flex-col items-center justify-center py-4">
                    <div className="w-32 h-16 overflow-hidden relative mb-2">
                      <div className="w-32 h-32 rounded-full border-[10px] border-[#1A1F2E] border-t-[#10B981] border-r-[#10B981] absolute top-0 left-0" style={{ transform: 'rotate(-45deg)', boxShadow: '0 0 25px rgba(16, 185, 129, 0.15)' }}></div>
                    </div>
                    <div className="text-center mt-[-10px]">
                      <div className="text-3xl text-[#10B981] font-bold tracking-wider drop-shadow-lg">75% BULLISH</div>
                    </div>
                  </div>
                </div>

                <div className="bg-black/40 p-5 rounded-lg border border-white/5 backdrop-blur-sm flex-1">
                  <h3 className="font-bold text-xs text-[#D4A537] mb-4 tracking-widest uppercase flex items-center gap-2">
                    <BarChart2 className="w-4 h-4" />
                    Order Flow
                  </h3>
                  <div className="space-y-4 text-[11px] font-mono">
                    <div className="flex justify-between items-center text-gray-400 border-b border-white/5 pb-2">
                      <span>LEVEL</span>
                      <span>VOLUME</span>
                    </div>
                    <div className="flex justify-between text-[#ef4444]"><span>2345.50</span><span>842K</span></div>
                    <div className="flex justify-between text-[#ef4444]"><span>2345.25</span><span>315K</span></div>
                    <div className="flex justify-between text-[#10B981] font-bold bg-[#10B981]/10 px-2 py-1 rounded border border-[#10B981]/20"><span>2345.00</span><span>1.2M</span></div>
                    <div className="flex justify-between text-[#10B981]"><span>2344.75</span><span>920K</span></div>
                  </div>
                </div>
              </div>

              {/* Right Side Mockup */}
              <div className="md:col-span-8 bg-gradient-to-br from-black/40 to-black/60 relative p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="font-bold text-xs text-[#D4A537] tracking-widest uppercase">Institutional Order Blocks</h3>
                </div>
                <div className="relative h-full w-full border-l border-b border-white/5 flex items-end justify-between px-2 pb-0 pt-10 min-h-[300px]">
                  <div className="flex items-end justify-around w-full h-[70%] px-4 gap-2 opacity-80">
                    <div className="candlestick h-[40%] shadow-[0_0_10px_#10B981]"></div>
                    <div className="candlestick bear h-[35%] shadow-[0_0_10px_#ef4444]"></div>
                    <div className="candlestick h-[50%] shadow-[0_0_10px_#10B981]"></div>
                    <div className="candlestick h-[55%] shadow-[0_0_10px_#10B981]"></div>
                    <div className="candlestick bear h-[45%] shadow-[0_0_10px_#ef4444]"></div>
                    <div className="candlestick h-[60%] shadow-[0_0_10px_#10B981]"></div>
                    <div className="candlestick h-[75%] shadow-[0_0_10px_#10B981]"></div>
                    <div className="candlestick bear h-[65%] shadow-[0_0_10px_#ef4444]"></div>
                    <div className="candlestick h-[80%] shadow-[0_0_10px_#10B981]"></div>
                    <div className="candlestick h-[85%] shadow-[0_0_10px_#10B981]"></div>
                  </div>
                </div>
              </div>

              {/* The Blur Overlay */}
              <div className="absolute bottom-0 left-0 w-full h-[60%] backdrop-blur-md bg-gradient-to-t from-[#0A0E17] via-[#0A0E17]/95 to-transparent z-20 flex flex-col items-center justify-center">
                <button onClick={handlePricingNavigation} className="group absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-10 py-5 bg-[#D4A537] text-black font-bold text-lg tracking-[2px] uppercase rounded-lg shadow-[0_0_40px_rgba(212,165,55,0.4)] transition-all duration-300 hover:scale-105 hover:shadow-[0_0_60px_rgba(212,165,55,0.6)] z-30 flex items-center gap-3">
                  <Lock className="w-5 h-5" />
                  UNLOCK FULL ANALYSIS
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Academy/Knowledge Section - FTMO Style Recreation */}
      <section className="py-16 lg:py-24 bg-gradient-to-b from-[#0A0E17] to-[#0F1419] relative w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10 relative z-10">

          {/* THE MAIN CARD */}
          <div
            style={{
              position: "relative",
              borderRadius: "16px",
              border: "1px solid rgba(255,255,255,0.08)",
              overflow: "hidden",
              background: "#131315",
              minHeight: "clamp(0px, 50vw, 680px)",
              display: "grid",
            }}
            className="ff-academy-card"
          >
            {/* TEXT COLUMN */}
            <div className="ff-academy-text-col">
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "flex-start",
                  height: "100%",
                }}
              >
                <h2 className="text-3xl md:text-5xl font-black mb-6 tracking-tight z-10 relative mt-0">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-200 to-gray-400">Master The Markets</span><br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F0C060] via-[#D4A537] to-[#A07820]">With ForexFund</span>
                </h2>

                {/* Heading glow: centered under heading on mobile, right-side on desktop */}
                <div
                  className="ff-academy-heading-glow pointer-events-none"
                  style={{
                    position: 'absolute',
                    top: '48px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '220px',
                    height: '120px',
                    background: 'radial-gradient(ellipse 70% 60% at 50% 80%, rgba(212,165,55,0.22) 0%, rgba(212,165,55,0.06) 40%, transparent 100%)',
                    filter: 'blur(26px)',
                    mixBlendMode: 'screen',
                    zIndex: 9,
                  }}
                />

                <p className="text-gray-400 text-lg leading-relaxed mb-8 z-10 relative mt-0">
                  Enhance your trading edge and learn to decode institutional order flow, fundamental analysis, and AI-driven signals with our comprehensive platform.
                </p>

                <ul className="space-y-4 mb-10 z-10 relative w-full" style={{ listStyle: "none", padding: 0, margin: "0 0 48px 0", display: "flex", flexDirection: "column", gap: "16px" }}>
                  {[
                    "Institutional-grade analysis at your fingertips",
                    "Perfect for beginner and advanced traders",
                    "Real-time AI signals with exact Entry, SL & TP"
                  ].map((item, idx) => (
                    <li key={idx} style={{ display: "inline-flex", alignItems: "center", gap: "16px", paddingLeft: "0" }}>
                      <div className="w-6 h-6 rounded-full bg-[#D4A537]/20 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-4 h-4 text-[#D4A537]" />
                      </div>
                      <span style={{ color: "rgba(255,255,255,0.8)", fontSize: "16px", fontWeight: 500 }}>
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={handlePricingNavigation}
                  className="inline-flex items-center gap-3 px-8 py-4 rounded-lg font-bold text-sm tracking-widest uppercase text-black transition-all duration-300 group z-10 relative hover:scale-105"
                  style={{
                    background: 'linear-gradient(135deg,#F0C060,#D4A537,#A07820)',
                    boxShadow: '0 0 30px rgba(212,165,55,0.3)',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.filter = 'none'; e.currentTarget.style.transform = 'none'; }}
                >
                  <PlayCircle className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  START ANALYZING NOW
                </button>
              </div>
            </div>

            <div className="ff-academy-video-col">
              <LazyVideo
                sources={academyVideos[currentAcademyVideo]}
                className="ff-academy-video"
                style={{ pointerEvents: "none", objectFit: "cover", objectPosition: "left center", width: "100%", height: "100%" }}
                priority={false}
                onEnded={handleAcademyVideoEnded}
              />

              {/* 1. PERFECT SEAMLESS DARK FADE - Matches FTMO exactly */}
              {/* Mobile Bottom Fade MUST fade fully to solid background color before the video ends */}
              <div
                className="block md:hidden absolute bottom-[-2px] left-0 w-full h-[200px] pointer-events-none z-[11]"
                style={{
                  background: "linear-gradient(to bottom, rgba(19,19,21,0) 0%, rgba(19,19,21,0.5) 30%, #131315 80%, #131315 100%)",
                  /* The last 20% of this overlay is pure solid #131315, meaning the bottom edge is strictly solid, making any straight line impossible */
                }}
              />
              {/* Desktop Right Fade */}
              <div
                className="hidden md:block absolute top-0 right-0 w-[50%] h-full pointer-events-none z-[3]"
                style={{
                  background: "linear-gradient(to right, rgba(19,19,21,0) 0%, rgba(19,19,21,0.8) 60%, #131315 100%)",
                }}
              />

              {/* No gold glow below the video on mobile so it perfectly matches the clean blend of FTMO */}

              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="ff-academy-bottom-glow hidden md:block"
                viewBox="0 0 1224 265"
                fill="none"
                style={{
                  position: "absolute",
                  bottom: "0",
                  left: "-10%",
                  height: "auto",
                  width: "120%",
                  pointerEvents: "none",
                  zIndex: 10,
                }}
              >
                <g filter="url(#filter0_f_gold)">
                  <path
                    d="M0 68.7856L473.196 184.502L1224 68.7856V361.786H0V68.7856Z"
                    fill="url(#paint0_linear_gold)"
                  />
                </g>
                <defs>
                  <filter
                    id="filter0_f_gold"
                    x="-68" y="0.785645"
                    width="1360" height="429"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                  >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                    <feGaussianBlur stdDeviation="50" result="effect1_foregroundBlur_gold" />
                  </filter>
                  <linearGradient
                    id="paint0_linear_gold"
                    x1="139.686" y1="361.786"
                    x2="180.513" y2="85.4154"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#D4A537" />
                    <stop offset="1" stopColor="#D4A537" stopOpacity="0" />
                  </linearGradient>
                </defs>
              </svg>

              {/* RIGHT GOLD GLOW - Desktop Only - Removed to avoid affecting texts */}
              <div
                className="hidden md:block absolute top-0 right-0 w-[40%] h-full pointer-events-none z-[10]"
                style={{
                  display: "none"
                }}
              />
            </div>
          </div>
        </div>

        <style>{`
          .ff-academy-card {
            grid-template-columns: 1fr;
            grid-template-rows: auto auto;
          }
          @media (min-width: 768px) {
            .ff-academy-card {
              grid-template-columns: repeat(12, 1fr);
              min-height: 680px;
              background: #131315;
            }
          }

          .ff-academy-text-col {
            order: 2;
            padding: 20px 30px 40px 30px;
            position: relative;
            z-index: 10;
            margin-top: -30px; /* Pulls text section up into the faded video area to guarantee 100% seamless blend */
          }
          @media (min-width: 768px) {
            .ff-academy-text-col {
              order: 1;
              grid-column: 7 / span 6; /* strictly right half */
              padding: 40px;
              margin-top: 0; /* Reset for desktop */
            }
          }
          @media (min-width: 1024px) {
            .ff-academy-text-col {
              grid-column: 7 / span 6;
              left: 3%; /* moved slightly right */
            }
          }
          @media (min-width: 1280px) {
            .ff-academy-text-col {
              grid-column: 7 / span 6;
              left: 5%; /* moved further right */
            }
          }

          .ff-academy-video-col {
            order: 1;
            position: relative;
            overflow: visible; /* Let fades bleed and combine seamlessly */
            height: 380px; /* Reduced to pull video up and avoid text overlap */
            border-radius: 16px 16px 0 0;
            z-index: 1;
            margin-bottom: 0px;
          }
          @media (min-width: 768px) {
            .ff-academy-video-col {
              order: 2;
              position: absolute;
              top: 0; left: 0;
              height: 100%;
              width: 60%; /* Slightly wider to let the fade work better */
              border-radius: 16px;
              margin-bottom: 0;
              overflow: hidden; /* Desktop keeps hidden */
            }
          }

          .ff-academy-video {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: left center;
            border-radius: 16px 16px 0 0; /* Ensures video corners are rounded when col overflow is visible */
          }
          @media (min-width: 768px) {
            .ff-academy-video {
              border-radius: 16px;
            }
          }
          @media (min-width: 1024px) {
            .ff-academy-video {
              min-height: 100%;
              position: relative;
              left: 0;
              width: 100%;
              object-position: left center;
            }
          }

          /* Heading glow styles */
          .ff-academy-heading-glow {
            display: block;
          }
          /* Mobile: center glow under heading */
          @media (max-width: 767px) {
            .ff-academy-heading-glow {
              position: absolute;
              top: 48px; /* aligns right below heading */
              left: 50%;
              transform: translateX(-50%);
              width: 220px;
              height: 120px;
              background: radial-gradient(ellipse 70% 60% at 50% 80%, rgba(212,165,55,0.22) 0%, rgba(212,165,55,0.06) 40%, transparent 100%)";
              filter: blur(26px);
              mix-blend-mode: screen;
              z-index: 9;
            }
          }
          /* Desktop/tablet: move glow to the right side and vertically center it relative to the heading */
          @media (min-width: 768px) {
            .ff-academy-heading-glow {
              position: absolute;
              right: -6%;
              top: 50%;
              transform: translateY(-50%);
              width: 260px;
              height: 220px;
              background: radial-gradient(ellipse 60% 50% at 50% 60%, rgba(212,165,55,0.22) 0%, rgba(212,165,55,0.06) 30%, transparent 100%)";
              filter: blur(32px);
              mix-blend-mode: screen;
              z-index: 9;
              pointer-events: none;
            }
          }

          .ff-academy-bottom-glow {
            display: block; /* Forced to show everywhere */
          }
          @media (min-width: 768px) {
            .ff-academy-bottom-glow {
              display: block;
            }
          }
        `}</style>
      </section >

      {/* Pricing Section */}
      < section className="py-32 bg-gradient-to-b from-[#0F1419] to-[#0A0E17] relative border-t border-white/5" >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#D4A537]/5 blur-[150px] rounded-full pointer-events-none z-[0]"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            {/* Golden Spotlight Effect */}
            <SpotlightGold />
            <h2
              className="text-3xl md:text-5xl font-black mb-4 uppercase tracking-tight relative z-10"
            >
              <span className="text-white" style={{ textShadow: '0 0 14px rgba(255,255,255,0.4)' }}>Institutional Grade </span>
              <span
                className="text-[#D4A537]"
                style={{ textShadow: '0 0 12px rgba(212,165,55,0.8), 0 0 25px rgba(212,165,55,0.4)' }}
              >
                Pricing
              </span>
            </h2>
            <p className="text-gray-400 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
              Select your level of market access.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-center">

            {/* Pro Plan — $30 */}
            <div className="glass-card rounded-2xl p-8 flex flex-col h-full hover:border-[#6366f1]/40 hover:-translate-y-2 hover:shadow-[0_10px_40px_-10px_rgba(99,102,241,0.2)] transition-all duration-500">
              <div className="text-[11px] font-black uppercase tracking-[2px] text-[#6366f1] mb-1">Think like a hedge fund</div>
              <h3 className="text-2xl text-white font-bold mb-2">PRO</h3>
              <div className="text-4xl text-white font-bold mb-4">$30<span className="text-sm text-gray-500 font-normal font-sans">/mo</span></div>
              <p className="text-gray-400 mb-8 text-sm">Full institutional AI analysis suite — 70 analyses/month, no signals.</p>
              <ul className="space-y-4 mb-8 flex-1">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#6366f1] w-5 h-5 mt-0.5" />
                  <span className="text-gray-300">70 AI Analyses / Month</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#6366f1] w-5 h-5 mt-0.5" />
                  <span className="text-gray-300">Fundamental Analysis Dashboard</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#6366f1] w-5 h-5 mt-0.5" />
                  <span className="text-gray-300">Order Flow (Order Blocks + FVGs)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#6366f1] w-5 h-5 mt-0.5" />
                  <span className="text-gray-300">COT Report Visual Dashboard</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#6366f1] w-5 h-5 mt-0.5" />
                  <span className="text-gray-300">AI Chart &amp; Market Structure</span>
                </li>
              </ul>
              <button
                onClick={() => handleSubscribe('Pro')}
                disabled={loading === 'Pro'}
                className="w-full py-4 rounded-lg border border-[#6366f1]/30 bg-[#6366f1]/10 text-[#6366f1] font-bold tracking-widest uppercase hover:bg-[#6366f1]/20 transition-all duration-300 disabled:opacity-50"
              >
                {loading === 'Pro' ? 'REDIRECTING...' : 'GET PRO'}
              </button>
            </div>

            {/* Signals Plan — $30 */}
            <div className="glass-card rounded-2xl p-8 flex flex-col h-full hover:border-[#229ED9]/40 hover:-translate-y-2 hover:shadow-[0_10px_40px_-10px_rgba(34,158,217,0.2)] transition-all duration-500">
              <div className="text-[11px] font-black uppercase tracking-[2px] text-[#229ED9] mb-1">Just the signals</div>
              <h3 className="text-2xl text-[#229ED9] font-bold mb-2">SIGNALS</h3>
              <div className="text-4xl text-white font-bold mb-4">$30<span className="text-sm text-gray-500 font-normal font-sans">/mo</span></div>
              <p className="text-gray-400 mb-8 text-sm">Actionable trade setups delivered via Telegram & Dashboard. No analysis tools.</p>
              <ul className="space-y-4 mb-8 flex-1">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#229ED9] w-5 h-5 mt-0.5" />
                  <span className="text-gray-300">Daily Gold (XAU/USD) Signals</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#229ED9] w-5 h-5 mt-0.5" />
                  <span className="text-gray-300">3-Layer Validation: Macro + Flow + COT</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#229ED9] w-5 h-5 mt-0.5" />
                  <span className="text-gray-300">Structural SL & TP Levels</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#229ED9] w-5 h-5 mt-0.5" />
                  <span className="text-gray-300">⚡ Autotrade MT5 Ready</span>
                </li>
              </ul>
              <button
                onClick={() => handleSubscribe('Signals')}
                disabled={loading === 'Signals'}
                className="w-full py-4 rounded-lg border border-[#229ED9]/30 bg-[#229ED9]/10 text-[#229ED9] font-bold tracking-widest uppercase hover:bg-[#229ED9]/20 transition-all duration-300 disabled:opacity-50"
              >
                {loading === 'Signals' ? 'REDIRECTING...' : 'GET SIGNALS'}
              </button>
            </div>

            {/* Premium Plan — $50 */}
            <div className="relative glass-card border-emerald-500/50 rounded-2xl p-8 flex flex-col h-full lg:scale-105 shadow-[0_0_50px_-15px_rgba(16,185,129,0.2)] z-10 hover:-translate-y-2 hover:shadow-[0_0_60px_-10px_rgba(16,185,129,0.3)] transition-all duration-500">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-emerald-500 text-black px-6 py-1.5 rounded-full text-[11px] font-bold tracking-widest uppercase shadow-[0_0_20px_rgba(16,185,129,0.5)]">
                Most Popular
              </div>
              <div className="text-[11px] font-black uppercase tracking-[2px] text-emerald-400 mb-1">Trade Gold like a pro</div>
              <h3 className="text-2xl text-emerald-400 font-bold mb-2">PREMIUM</h3>
              <div className="text-4xl text-white font-bold mb-4">$50<span className="text-sm text-gray-500 font-normal font-sans">/mo</span></div>
              <p className="text-gray-400 mb-8 text-sm">70 AI analyses + verified Gold (XAU/USD) signals with exact Entry, SL &amp; TP.</p>
              <ul className="space-y-4 mb-8 flex-1">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-emerald-400 w-5 h-5 mt-0.5 drop-shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
                  <span className="text-white">70 AI Analyses / Month</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-emerald-400 w-5 h-5 mt-0.5 drop-shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
                  <span className="text-white">3 Gold (XAU/USD) Signals / Day</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-emerald-400 w-5 h-5 mt-0.5 drop-shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
                  <span className="text-white">3-Layer Validation: Macro + Flow + COT</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-emerald-400 w-5 h-5 mt-0.5 drop-shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
                  <span className="text-white">Structural SL &amp; TP Levels</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-emerald-400 w-5 h-5 mt-0.5 drop-shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
                  <span className="text-white">⚡ Autotrade MT5 Ready</span>
                </li>
              </ul>
              <button
                onClick={() => handleSubscribe('Premium')}
                disabled={loading === 'Premium'}
                className="w-full py-4 rounded-lg bg-gradient-to-r from-emerald-600 to-emerald-400 text-black font-bold tracking-widest uppercase shadow-[0_0_20px_rgba(16,185,129,0.3)] hover:shadow-[0_0_30px_rgba(16,185,129,0.5)] hover:brightness-110 transition-all duration-300 disabled:opacity-50"
              >
                {loading === 'Premium' ? 'REDIRECTING...' : 'GET PREMIUM'}
              </button>
            </div>

            {/* Elite Plan — $100 */}
            <div className="glass-card rounded-2xl p-8 flex flex-col h-full hover:border-[#D4A537]/40 hover:-translate-y-2 hover:shadow-[0_10px_40px_-10px_rgba(212,165,55,0.2)] transition-all duration-500">
              <div className="text-[11px] font-black uppercase tracking-[2px] text-[#D4A537] mb-1">Maximum edge. No limits.</div>
              <h3 className="text-2xl text-[#D4A537] font-bold mb-2">ELITE</h3>
              <div className="text-4xl text-white font-bold mb-4">$100<span className="text-sm text-gray-500 font-normal font-sans">/mo</span></div>
              <p className="text-gray-400 mb-8 text-sm">Everything in Premium + 250 analyses, 90%+ conviction elite-only signal setups.</p>
              <ul className="space-y-4 mb-8 flex-1">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#D4A537] w-5 h-5 mt-0.5 drop-shadow-[0_0_8px_rgba(212,165,55,0.5)]" />
                  <span className="text-white font-semibold">Everything in Premium</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#D4A537] w-5 h-5 mt-0.5 drop-shadow-[0_0_8px_rgba(212,165,55,0.5)]" />
                  <span className="text-white">250 AI Analyses / Month</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#D4A537] w-5 h-5 mt-0.5 drop-shadow-[0_0_8px_rgba(212,165,55,0.5)]" />
                  <span className="text-white">90%+ Confidence Elite Signals</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#D4A537] w-5 h-5 mt-0.5 drop-shadow-[0_0_8px_rgba(212,165,55,0.5)]" />
                  <span className="text-white">Autotrade with Dynamic Sizing</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#D4A537] w-5 h-5 mt-0.5 drop-shadow-[0_0_8px_rgba(212,165,55,0.5)]" />
                  <span className="text-white">Priority Signal Delivery</span>
                </li>
              </ul>
              <button
                onClick={() => handleSubscribe('Elite')}
                disabled={loading === 'Elite'}
                className="w-full py-4 rounded-lg bg-gradient-to-r from-[#F0C060] via-[#D4A537] to-[#A07820] text-black font-bold tracking-widest uppercase shadow-[0_0_20px_rgba(212,165,55,0.3)] hover:shadow-[0_0_30px_rgba(212,165,55,0.5)] hover:brightness-110 transition-all duration-300 disabled:opacity-50"
              >
                {loading === 'Elite' ? 'REDIRECTING...' : 'GET ELITE'}
              </button>
            </div>


          </div>
        </div>
      </section >

      {/* Telegram Community Section */}
      <section className="py-24 relative overflow-hidden bg-[#060810]">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none hero-grid"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="glass-card rounded-[2rem] p-8 md:p-16 border-[#229ED9]/30 bg-gradient-to-br from-[#229ED9]/10 via-transparent to-transparent flex flex-col md:flex-row items-center gap-12">
            <div className="flex-1 text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 border border-[#229ED9]/30 rounded-full bg-[#229ED9]/10">
                <MessageCircle className="w-4 h-4 text-[#229ED9]" />
                <span className="text-xs font-black tracking-[2px] uppercase text-[#229ED9]">Free Community Access</span>
              </div>
              <h2 className="text-4xl md:text-6xl font-black text-white mb-6 uppercase tracking-tight leading-none">
                Join our <span className="text-[#229ED9]">Telegram</span> <br />
                Community
              </h2>
              <p className="text-gray-400 text-lg md:text-xl leading-relaxed mb-10 max-w-xl">
                Get daily market bias, institutional levels, and community trade ideas—all for free. Join thousands of traders in our official Telegram channel today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <a
                  href="https://t.me/forexfundai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-10 py-5 rounded-xl bg-[#229ED9] text-white font-black text-sm tracking-[2px] uppercase flex items-center justify-center gap-3 shadow-[0_0_40px_rgba(34,158,217,0.4)] hover:scale-105 transition-all"
                >
                  <MessageCircle className="w-5 h-5" />
                  Join Free Group
                </a>
              </div>
            </div>
            <div className="relative w-full max-w-md">
              <div className="absolute inset-0 bg-[#229ED9] blur-[100px] opacity-20 pointer-events-none"></div>
              <div className="relative z-10 p-8 glass-card rounded-3xl border-[#229ED9]/20 transform rotate-3 hover:rotate-0 transition-transform duration-700">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-full bg-[#229ED9] flex items-center justify-center shadow-lg">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-white font-bold text-lg">ForexFund AI</div>
                    <div className="text-[#229ED9] text-xs font-bold">@forexfundai • 12.4K Members</div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="text-gray-400 text-[10px] uppercase font-bold mb-2">Daily Bias: XAUUSD</div>
                    <div className="text-[#10B981] font-bold">BULLISH BIAS CONFIRMED (92%)</div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="text-gray-400 text-[10px] uppercase font-bold mb-2">Institutional Zone</div>
                    <div className="text-[#D4A537] font-bold">REJECTION AT 2345.00 OB</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      < section className="py-24 bg-gradient-to-b from-[#0A0E17] to-[#131315] border-t border-white/10" >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-black mb-4 text-center tracking-tight text-white">Frequently Asked Questions</h2>
          <p className="text-center text-gray-400 mb-10 text-lg">Find your answers here!</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                q: 'What is ForexFund and how does it work?',
                a: 'ForexFund is an AI-powered institutional analysis platform for forex and commodities. It synthesizes macro data, order flow, COT reports, and technical trends to give traders a clear directional bias and actionable signals.'
              },
              {
                q: 'How is your AI different from other trading tools?',
                a: 'Our AI combines real-time macroeconomic data, institutional order flow, and CFTC COT positioning into a single dashboard, providing a true institutional edge rather than just technical signals.'
              },
              {
                q: 'What markets do you cover?',
                a: 'We cover major forex pairs (EUR/USD, GBP/USD, USD/JPY, AUD/USD), Gold (XAU/USD), and other high-liquidity instruments.'
              },
              {
                q: 'How often are signals and analysis updated?',
                a: 'Signals and dashboards are updated in real time, with COT data refreshed weekly after the official CFTC release.'
              },
              {
                q: 'Can beginners use ForexFund?',
                a: 'Yes! The platform is designed for both beginners and advanced traders, with clear visual dashboards and educational resources.'
              },
              {
                q: 'Do you offer backtesting or historical analysis?',
                a: 'Yes, our Price History & Trends module lets you view 1-year historical charts and technical overlays for any supported instrument.'
              },
              {
                q: 'Is my data and account secure?',
                a: 'We use industry-standard encryption and secure authentication to protect your account and personal data.'
              },
              {
                q: 'How do I access institutional order flow data?',
                a: 'Order flow is mapped using AI from public and proprietary sources, visualized as order blocks, liquidity zones, and fair value gaps.'
              },
              {
                q: 'What is the cost of using ForexFund?',
                a: 'We offer transparent institutional-grade pricing. See our Pricing section above for details.'
              },
              {
                q: 'Where can I learn more about your technology?',
                a: 'Visit our About page for a deep dive into our AI models, data sources, and institutional analysis methodology.'
              }
            ].map((faq, idx) => (
              <div
                key={idx}
                className="bg-gradient-to-br from-[#D4A537]/20 to-[#23242A] rounded-xl p-6 border border-[#D4A537]/40 shadow-lg flex items-center gap-4 cursor-pointer transition-all duration-200 hover:border-[#D4A537]"
                onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                tabIndex={0}
                role="button"
                aria-label={faq.q}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setOpenFaq(openFaq === idx ? null : idx);
                  }
                }}
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#D4A537] text-black text-2xl font-bold mr-2 select-none">
                  {openFaq === idx ? '−' : '+'}
                </span>
                <div className="flex-1">
                  <h3 className="text-lg md:text-xl font-bold text-white mb-2">{faq.q}</h3>
                  {openFaq === idx && (
                    <p className="text-[#D4A537] text-base leading-relaxed">{faq.a}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <a href="/about" className="btn-gold px-8 py-4 rounded-lg text-base font-bold uppercase tracking-wider shadow-lg inline-block transition-all duration-300 hover:scale-105">
              Explore Our Institutional Philosophy
            </a>
          </div>
        </div>
      </section >

    </div >
  );
};
